// File: src/pages/DashboardUsers.js

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate }                    from 'react-router-dom';
import AuthContext                        from '../context/AuthContext';
import { fetchUsers }                     from '../api/users';
import '../assets/css/DashboardUsers.css';

const DashboardUsers = () => {
  const navigate       = useNavigate();
  const { logout }     = useContext(AuthContext);

  const [users, setUsers]           = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, per_page: 20, current_page: 1, total_pages: 1
  });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [modalImage, setModalImage] = useState(null);
  const [openDropdownFor, setOpenDropdownFor] = useState(null);

  const loadPage = async (page = 1) => {
    setLoading(true); setError('');
    try {
      const res = await fetchUsers(page, pagination.per_page);
      setUsers(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load users.');
      if (/auth/i.test(err.message)) {
        await logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(pagination.current_page);
    // eslint-disable-next-line
  }, []);

  const handlePage = newPage => {
    if (newPage < 1 || newPage > pagination.total_pages) return;
    loadPage(newPage);
  };

  const openModal = url => setModalImage(url);
  const closeModal = () => setModalImage(null);
  const toggleDropdown = id =>
    setOpenDropdownFor(openDropdownFor === id ? null : id);

  return (
    <div className="dashboard-users">
      <h2>{pagination.total} Users</h2>
      <p>Below is the list of users. Click an avatar to view full size.</p>

      {loading && <p>Loading users…</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <>
          <div className="users-table">
            <div className="table-header">
              <span>ID</span>
              <span>Full Name</span>
              <span>Username</span>
              <span>Last Login</span>
              <span>Date Joined</span>
              <span>Status</span>
              <span>Actions</span>
            </div>

            {users.map(u => (
              <div className="table-row" key={u.user_id}>
                <span>{u.user_id}</span>
                <span className="name-cell">
                  <img
                    src={u.profile_picture_url}
                    alt={u.username}
                    className="user-avatar"
                    onClick={() => openModal(u.profile_picture_url)}
                  />
                  {u.full_name}
                </span>
                <span>{u.username}</span>
                <span>{u.last_login || '-'}</span>
                <span>{new Date(u.date_joined).toLocaleString()}</span>
                <span className={`status ${u.account_status}`}>
                  {u.account_status}
                </span>
                <span className="actions-cell">
                  <button
                    className="action-btn"
                    onClick={() => toggleDropdown(u.user_id)}
                  >
                    ⋮
                  </button>
                  {openDropdownFor === u.user_id && (
                    <div className="dropdown-menu">
                      <button onClick={() => navigate(`/dashboard/users/${u.user_id}/edit`)}>
                        Edit
                      </button>
                      <button onClick={() => navigate(`/dashboard/users/${u.user_id}/delete`)}>
                        Delete
                      </button>
                      <button onClick={() => navigate(`/dashboard/users/${u.user_id}/block`)}>
                        Block
                      </button>
                      <button onClick={() => navigate(`/dashboard/users/${u.user_id}/contact`)}>
                        Contact
                      </button>
                    </div>
                  )}
                </span>
              </div>
            ))}
          </div>

          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => handlePage(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => handlePage(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.total_pages}
            >
              Next
            </button>
          </div>
        </>
      )}

      {modalImage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <img src={modalImage} alt="Avatar full size" />
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardUsers;
